import { Injectable, OnDestroy } from '@angular/core'
import { Job, UniversityUserRole } from '@sciper/api-client'
import { BehaviorSubject, Subject, takeUntil } from 'rxjs'
import { AuthService } from '../auth/auth.service'
import { JobService } from '../job/job.service'
import { UserService } from '../user/user.service'

const JOB_STORAGE_KEY = 'job-edit-'
const UNIVERSITY_STORAGE_KEY = 'admin-selected-university'

@Injectable({
  providedIn: 'root',
})
export class AdministrationService implements OnDestroy {
  private _destroySubject$: Subject<void> = new Subject<void>()

  job: BehaviorSubject<Job> = new BehaviorSubject<Job>(undefined)
  ownUniversityUserRoles = new BehaviorSubject<UniversityUserRole[]>(undefined)

  selectedUniversity: BehaviorSubject<string> = new BehaviorSubject<string>(
    undefined
  )

  get isAdmin(): boolean {
    return this.authService.isAdmin
  }

  get showAdministrationMenu(): boolean {
    return this.ownUniversityUserRoles.value?.length > 0 || this.isAdmin
  }

  get canChangeUserRoles(): boolean {
    return (
      this.ownUniversityUserRoles.value?.some(
        (role) =>
          role.universityId === this.selectedUniversity.value &&
          role.role === UniversityUserRole.RoleEnum.Owner
      ) || this.isAdmin
    )
  }

  constructor(
    private jobService: JobService,
    private userService: UserService,
    private authService: AuthService
  ) {
    this.selectedUniversity
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((university) => this.cacheSelectedUniversity(university))
    this.jobService.job
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((job) => this.job.next(job))

    this.userService.universityUserRoles
      .pipe(takeUntil(this._destroySubject$))
      .subscribe((roles) => {
        if (roles?.length) {
          this.ownUniversityUserRoles.next(roles)
          if (!this.selectedUniversity.value) {
            // set default university
            this.selectedUniversity.next(roles[0].universityId)
          }
        }
      })

    this.loadCachedUniversity()
  }

  ngOnDestroy() {
    this._destroySubject$.next()
    this._destroySubject$.complete()
  }

  hasRole(neededRole: UniversityUserRole.RoleEnum) {
    return this.ownUniversityUserRoles.value?.some(
      (role) =>
        role.universityId === this.selectedUniversity.value &&
        role.role === neededRole
    )
  }

  fetchJob(jobId: string) {
    const jobJson = localStorage.getItem(this.getStorageKey(jobId))
    if (jobJson) {
      const job = JSON.parse(jobJson) as Job
      this.job.next(job)
      return
    }

    this.jobService.fetchJob(jobId)
  }

  cacheJobEdit(job: Job) {
    const jobJson = JSON.stringify(job)
    localStorage.setItem(this.getStorageKey(job.jobId), jobJson)
  }

  private cacheSelectedUniversity(universityId: string) {
    if (universityId === undefined) {
      return
    }
    localStorage.setItem(UNIVERSITY_STORAGE_KEY, universityId)
  }

  private loadCachedUniversity() {
    const universityId = localStorage.getItem(UNIVERSITY_STORAGE_KEY)
    if (universityId) {
      this.selectedUniversity.next(universityId)
    }
  }

  private getStorageKey(jobId: string) {
    return `${JOB_STORAGE_KEY}${jobId}`
  }
}
